/** @file Evaluates all tracks into a pose: reset to setup, blend samples, then updatePose. */
import { color } from '@pose2d/math/color';
import { invariant } from '@pose2d/std/invariant';
import { animTime } from '../_internal/animTime';
import { applyDrawOrderOps } from '../_internal/applyDrawOrderOps';
import { commitTrack } from '../_internal/commitTrack';
import { crossfadeSample } from '../_internal/crossfadeSample';
import { fixLayerWeight } from '../_internal/direction/fixLayerWeight';
import { ownerShare } from '../_internal/direction/ownerShare';
import { layerEntry } from '../_internal/layerEntry';
import { mixWeight } from '../_internal/mixWeight';
import { sampleIndex } from '../_internal/timeline/sampleIndex';
import { applyDiscreteChannels } from '../anim/applyDiscreteChannels';
import { blendSample } from '../anim/blendSample';
import { sampleAnimation } from '../anim/sampleAnimation';
import { POSE_LAYOUT as L } from '../contracts/pose';
import type { Pose } from '../contracts/pose';
import type { Sample } from '../contracts/sample';
import type { AnimState, TrackEntry } from '../contracts/state';
import { deformSize } from '../pose/deformSize';
import { resetPose } from '../pose/resetPose';
import { updatePose } from '../pose/updatePose';
import type { SkeletonData } from '../skeletonData';
import { BASE_TRACKS } from './baseTracks.data';

/** Number of entries the entry still mixes from (0 = no crossfade). */
function chainDepth(entry: TrackEntry): number {
  let n = 0;
  for (let e = entry.from; e !== null; e = e.from) n++;
  return n;
}

/** The entry k links down the from-chain (0 = the entry itself). */
function entryAt(entry: TrackEntry, k: number): TrackEntry {
  let e = entry;
  for (let i = 0; i < k && e.from !== null; i++) e = e.from;
  return e;
}

/** Reused byte buffer for color.unpack (R, G, B, A), so loadBase stays allocation-free. */
const BYTES: [number, number, number, number] = [0, 0, 0, 0];

/** base := the setup pose resetPose just wrote (continuous channels as values, colour bytes as 0..1 floats per W2-08 R1); nothing keyed. */
function loadBase(base: Sample, pose: Pose, slotStart: number, ikStart: number): void {
  for (let i = 0; i < slotStart; i++) base.values[i] = pose.local[i] ?? 0;
  for (let s = 0; s < pose.slotColor.length; s++) {
    const o = slotStart + s * L.SLOT_STRIDE;
    color.unpack(pose.slotColor[s] ?? 0, BYTES);
    base.values[o + L.SLOT_R] = BYTES[0] / 255;
    base.values[o + L.SLOT_G] = BYTES[1] / 255;
    base.values[o + L.SLOT_B] = BYTES[2] / 255;
    base.values[o + L.SLOT_ALPHA] = BYTES[3] / 255;
    color.unpack(pose.slotDark[s] ?? 0, BYTES);
    base.values[o + L.SLOT_DARK_R] = BYTES[0] / 255;
    base.values[o + L.SLOT_DARK_G] = BYTES[1] / 255;
    base.values[o + L.SLOT_DARK_B] = BYTES[2] / 255;
  }
  for (let i = 0; i < pose.ik.length; i++) base.values[ikStart + i] = pose.ik[i] ?? 0;
  base.mask.fill(0);
  base.inherit.fill(-1);
  base.attachments.fill(-2);
  base.drawOrderKeyed = false;
  base.deformKeyed.fill(0);
}

/** Whether every deform buffer of s has its attachment's deformSize (a mesh with another vertex count does not fit). */
function deformFits(s: Sample, data: SkeletonData): boolean {
  if (s.deform.length !== data.attachments.length) return false;
  for (let i = 0; i < s.deform.length; i++) {
    const att = data.attachments[i];
    if (att === undefined || s.deform[i]?.length !== deformSize(att)) return false;
  }
  return true;
}

/** Whether s was created by createSample(data) for this skeleton (`end` = its channel count); no allocation. */
function fits(s: Sample, data: SkeletonData, end: number): boolean {
  return s.values.length === end && s.inherit.length === data.bones.length && s.attachments.length === data.slots.length && deformFits(s, data);
}

/**
 * Samples the entry and its from-chain deepest first, layers and crossfades them, then commits into base. R10-06:
 * the scratch samples must be distinct objects (`Array(3).fill(sample)` or `[s0, s1, s1]` would sample over the
 * running base or crossfade a sample with itself) sized for this skeleton, and every link's mixDirs must cover
 * every bone. On a base track above 0 the deepest link, which has nothing to mix from, fades in at mixWeight (m3);
 * on track 0 it is the bottom layer and shows at once (R19-01: a spawned character never blends out of its setup
 * pose). Every link layers at alpha × `weight`. A mirrorFix track (`fixOf` = its base track, else -1) gets `weight` =
 * the summed shares of its links' owners and crossfades link k in at share_k / (the shares of k and every link below
 * it), so each link shows at its owner's share of the base track (R19-02 follow-up).
 */
function applyTrack(state: AnimState, entry: TrackEntry, track: number, weight: number, data: SkeletonData, base: Sample, scratch: Sample[], slotStart: number, ikStart: number, end: number): void {
  const depth = chainDepth(entry);
  const fixOf = track >= BASE_TRACKS ? track - BASE_TRACKS : -1;
  const r = scratch[1];
  const s = scratch[depth > 0 ? 2 : 1];
  invariant(r !== undefined && s !== undefined, 'applyState: scratch needs 2 samples, 3 while a track crossfades');
  invariant(r !== base && (depth === 0 || (s !== r && s !== base)), 'applyState: scratch samples must be distinct objects (createSample(data) x 3)');
  invariant(fits(r, data, end) && fits(s, data, end), 'applyState: scratch samples must be sized for this skeleton (createSample(data))');
  let below = 0;
  for (let k = depth; k >= 0; k--) {
    const e = entryAt(entry, k);
    const target = k === depth ? r : s;
    const t = animTime(data, e);
    sampleAnimation(target, data, e.anim, t, t);
    layerEntry(target, base, data, e, slotStart, ikStart, fixOf < 0 && k === depth && track > 0 ? weight * mixWeight(e) : weight);
    const share = fixOf >= 0 && e.owner !== null ? ownerShare(state, fixOf, e.owner) : 0;
    below += share;
    if (k === depth) continue;
    invariant(e.mixDirs === null || e.mixDirs.length >= data.bones.length, 'applyState: TrackEntry.mixDirs must hold one value per bone');
    crossfadeSample(r, target, base, fixOf < 0 ? mixWeight(e) : below > 0 ? share / below : 1, e, slotStart, ikStart);
  }
  commitTrack(base, r);
}

/** Runtime equips override what the skin and the keys resolved (equip wins); Map.get only, no iterator. */
function applyEquips(pose: Pose, state: AnimState): void {
  if (state.equipped.size === 0) return;
  for (let s = 0; s < pose.slotAttachment.length; s++) {
    const item = state.equipped.get(s);
    if (item !== undefined) pose.slotAttachment[s] = item.attachment;
  }
}

/** The current direction's mirrorFix draw-order ops on top of the keyed (else slot) order. */
function applyDirectionFix(pose: Pose, state: AnimState, data: SkeletonData): void {
  if (state.direction === null || data.directions === null) return;
  const fix = data.directions.entries.get(state.direction);
  if (fix !== undefined && fix.mode === 'mirrorFix') applyDrawOrderOps(pose.drawOrder, fix.fixDrawOrderOps);
}

/**
 * W2-11: resetPose through the cached state.skin; per track (ascending) sample + layerEntry + crossfadeSample +
 * commitTrack into scratch[0]; blendSample(…, 1, 'replace') + applyDiscreteChannels; equips; the direction's
 * mirrorFix draw-order ops; updatePose. scratch = createSample(data) x 3: distinct objects sized for data (each
 * deform buffer at its attachment's deformSize), else an invariant fails (R10-06). Allocation-free; the only state written is the unwrapped rotation differences
 * crossfadeSample keeps in TrackEntry.mixDirs (CR-010).
 */
export function applyState(state: AnimState, data: SkeletonData, pose: Pose, scratch: Sample[]): void {
  resetPose(pose, data, state.skin);
  const slotStart = sampleIndex(data, 'slot', 0, 0);
  const ikStart = sampleIndex(data, 'ik', 0, 0);
  const end = sampleIndex(data, 'ik', data.ik.length, 0);
  let base: Sample | null = null;
  for (let t = 0; t < state.tracks.length; t++) {
    const entry = state.tracks[t];
    if (entry === null || entry === undefined || entry.delay > 0) continue;
    // A mirrorFix layer counts as much as its directional base entry still does in its base track (R19-02).
    const weight = t >= BASE_TRACKS ? fixLayerWeight(state, t - BASE_TRACKS) : 1;
    if (weight === 0) continue;
    if (base === null) {
      base = scratch[0] ?? null;
      invariant(base !== null, 'applyState: scratch is empty but a track is active');
      invariant(fits(base, data, end), 'applyState: scratch samples must be sized for this skeleton (createSample(data))');
      loadBase(base, pose, slotStart, ikStart);
    }
    applyTrack(state, entry, t, weight, data, base, scratch, slotStart, ikStart, end);
  }
  if (base !== null) {
    blendSample(pose, data, base, 1, 'replace');
    applyDiscreteChannels(pose, data, base, state.skin);
  }
  applyEquips(pose, state);
  applyDirectionFix(pose, state, data);
  updatePose(pose, data);
}
